import api from './api';

export interface GlobalSearchOrder {
  id: string;
  orderNumber: string;
  patientName: string;
  rxNumber: string;
  medicationName: string;
  workflowStage: string;
  status: string;
  deliveryDate: string;
  /** Present on every result. */
  pharmacyId?: string;
  /** Resolved by the backend from the GSP pharmacy registry (e.g. "GSP - Mississauga"). */
  pharmacyName?: string | null;
}

export interface GlobalSearchPatient {
  id: string;
  compositeId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phonePrimary: string;
  email: string;
  status: string;
  pharmacyId?: string;
  pharmacyName?: string | null;
  /** Cross-pharmacy only — most recent fill date from Kroll. */
  lastFillDate?: string | null;
  /** Cross-pharmacy only — active medication names from Kroll. */
  medications?: string[];
  /**
   * GP-1348 — name of the nursing/group home this patient belongs to, from the
   * synced Kroll NH membership. Null/absent when they're not in one; never a
   * placeholder home name.
   */
  nhGroupName?: string | null;
  /** Cross-pharmacy only — patient address (for disambiguation). */
  addressStreet?: string;
  addressUnit?: string;
  addressCity?: string;
  addressProvince?: string;
  addressPostalCode?: string;
  /**
   * Set when the patient surfaced via the patient_addresses sub-query —
   * the actual row that matched the search tokens. Frontend renders this
   * in place of the primary address so the PA sees the line they typed
   * against. Undefined when the patient was found by name / phone / etc.
   */
  matchedAddress?: {
    addressLine1: string;
    unitNumber: string | null;
    city: string;
    province: string;
    postalCode: string;
    addressType: string;
  };
}

export interface GlobalSearchSubscription {
  id: string;
  patientName: string;
  rxNumber: string;
  medicationName: string;
  subscriptionStatus: string;
  dueDate: string;
  nextDeliveryDate: string;
  pharmacyId?: string;
  pharmacyName?: string | null;
}

export interface GlobalSearchResults {
  orders: GlobalSearchOrder[];
  patients: GlobalSearchPatient[];
  subscriptions: GlobalSearchSubscription[];
}

/**
 * Pharmacy-scoped search. Returns only results from the user's currently
 * selected pharmacy (legacy / default mode).
 */
export async function searchAll(
  query: string,
  limit = 5,
  signal?: AbortSignal,
): Promise<GlobalSearchResults> {
  const { data } = await api.get<GlobalSearchResults>('/api/search', {
    params: { q: query, limit },
    signal,
  });
  return data;
}

/**
 * GP-314 — Cross-pharmacy universal search. Returns results from the 5
 * GSP-operated pharmacies only. Each row carries `pharmacyId` and a
 * server-resolved `pharmacyName` (e.g. "GSP - Mississauga") for the badge.
 */
export async function searchAllUniversal(
  query: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<GlobalSearchResults> {
  const { data } = await api.get<GlobalSearchResults>('/api/search/universal', {
    params: { q: query, limit },
    signal,
  });
  return data;
}

/**
 * Active-pharmacy Kroll fallback. Called only when the pharmacy-scoped DB
 * search returned no patient for a name-like query — hits the active
 * pharmacy's Kroll, syncs any matches into PHOX, and returns them so they're
 * found locally next time. Patient-only (orders/subscriptions always empty).
 * Best-effort: the caller swallows failures.
 */
export async function searchKrollFallback(
  query: string,
  limit = 5,
  signal?: AbortSignal,
): Promise<GlobalSearchResults> {
  const { data } = await api.get<GlobalSearchResults>('/api/search/kroll-fallback', {
    params: { q: query, limit },
    signal,
  });
  return data;
}
